import type { AssetsForLeadCursor, LeadsCursor, LeadsForAssetCursor } from '@aikoa/api'
import { type OwnershipLevel, type PropertyInterestData } from '@aikoa/common'
import { keepPreviousData, useInfiniteQuery, useMutation, useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'
import { useSession } from '~/components/user/SessionProvider'
import { trpc, trpcClient } from '~/lib/trpc'

const LEADS_PAGE_SIZE = 5

export function useLead(leadId: string) {
  const { session, isAnonymous } = useSession()
  const isAuthenticated = !!session?.user && !isAnonymous

  const authenticatedResult = useQuery(
    trpc.leads.get.queryOptions(
      { leadId },
      {
        enabled: !!leadId && isAuthenticated,
        placeholderData: keepPreviousData,
      }
    )
  )

  const publicResult = useQuery(
    trpc.leads.getPublic.queryOptions(
      { leadId },
      {
        enabled: !!leadId && !isAuthenticated,
        placeholderData: keepPreviousData,
      }
    )
  )

  const result = isAuthenticated ? authenticatedResult : publicResult

  return {
    ...result,
    isAuthenticatedDataReady:
      isAuthenticated && !authenticatedResult.isPlaceholderData && !authenticatedResult.isLoading,
  }
}

const PROPERTY_LEADS_PAGE_SIZE = 3

export function usePropertyLeads(assetId?: string) {
  const { data, isLoading, isRefetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['leads', 'matchingLeadsForAsset', assetId] as const,
      queryFn: async ({ pageParam }) => {
        return trpcClient.leads.matchingLeadsforAsset.query({
          assetId: assetId!,
          includeMarketplace: true,
          limit: PROPERTY_LEADS_PAGE_SIZE,
          cursor: pageParam,
        })
      },
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      initialPageParam: undefined as LeadsForAssetCursor | undefined,
      enabled: !!assetId,
    })

  const leads = useMemo(() => {
    if (!data?.pages) {
      return []
    }

    return data.pages.flatMap((page) => page.items)
  }, [data])

  const totalCount = data?.pages[0]?.totalCount ?? 0
  const matchCount = data?.pages[0]?.matchCount ?? 0

  return {
    leads,
    totalCount,
    matchCount,
    isLoading,
    isRefetching,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  }
}

const MATCHING_ASSETS_PAGE_SIZE = 3

export function useMatchingAssets(leadId: string) {
  const { data, isLoading, isRefetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['leads', 'matchingAssetsForLeads', leadId] as const,
      queryFn: async ({ pageParam }) => {
        return trpcClient.leads.matchingAssetsForLeads.query({
          leadId,
          limit: MATCHING_ASSETS_PAGE_SIZE,
          cursor: pageParam,
        })
      },
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      initialPageParam: undefined as AssetsForLeadCursor | undefined,
      enabled: !!leadId,
    })

  const matchingAssets = useMemo(() => {
    if (!data?.pages) {
      return []
    }

    return data.pages.flatMap((page) => page.items)
  }, [data])

  const totalCount = data?.pages[0]?.totalCount ?? 0
  const matchCount = data?.pages[0]?.matchCount ?? 0

  return {
    matchingAssets,
    totalCount,
    matchCount,
    isLoading,
    isRefetching,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  }
}

export function useLeads({
  ownershipLevel,
  matchAssetId,
  matchAssetData,
  fetchAllCounts = false,
}: {
  ownershipLevel: OwnershipLevel
  matchAssetId?: string
  matchAssetData?: Pick<PropertyInterestData, 'filters' | 'locations'>
  fetchAllCounts?: boolean
}) {
  const { data, isLoading, isRefetching, refetch, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ['leads', 'list', ownershipLevel, matchAssetId, matchAssetData] as const,
      queryFn: async ({ pageParam }) => {
        return trpcClient.leads.list.query({
          ownershipFilter: ownershipLevel,
          matchAssetId,
          matchAssetData,
          limit: LEADS_PAGE_SIZE,
          cursor: pageParam,
          fetchAllCounts,
        })
      },
      getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
      initialPageParam: undefined as LeadsCursor | undefined,
    })

  const leads = useMemo(() => {
    if (!data?.pages) {
      return []
    }

    return data.pages.flatMap((page) => page.items)
  }, [data])

  const totalCounts = data?.pages[0]?.totalCounts

  return {
    leads,
    totalCounts,
    isLoading,
    isRefetching,
    refetch,
    fetchNextPage,
    hasNextPage: hasNextPage ?? false,
    isFetchingNextPage,
  }
}

export function useMyOwnedLeads() {
  return useQuery(trpc.leads.myOwnedLeads.queryOptions())
}

export function useLeadContactRequestStatus(leadId: string) {
  return useQuery(
    trpc.leads.contactRequestStatus.queryOptions(
      { leadId },
      {
        enabled: !!leadId,
      }
    )
  )
}

export function useDeleteLead() {
  return useMutation(trpc.leads.deleteLead.mutationOptions())
}
